import type { VisHourCell } from "./visibility-strip";

export type MetricDetail = { kind: "visHours"; series: VisHourCell[] };

export type MetricCell = { label: string; value: string; sublabel?: string; detail?: MetricDetail };

export type SkiMetricInput = {
  snowfallCm48h: number | null;
  snowDepthCm: number | null;
  tempMinC: number | null;
  tempMaxC: number | null;
  windSpeedKmh: number | null;
  freezingLevelM: number | null;
  visibilityLabel: string | null;
  visibilityHours?: VisHourCell[];
};

export type MtbMetricInput = {
  apparentTempC: number | null;
  soilMoistureLabel: string | null;
  windSpeedKmh: number;
  precipMm24h: number | null;
};

export type CoastalMetricInput = {
  waveHeightM: number | null;
  swellPeriodS: number | null;
  swellDirLabel: string | null;
  windSpeedKmh: number | null;
  windLabel: string | null; // "offshore" | "onshore" | "cross" | null
  waterTempC: number | null;
  tideLabel: string | null;
};

export function buildSkiMetrics(i: SkiMetricInput): MetricCell[] {
  const cells: MetricCell[] = [
    { label: "New 48h", value: i.snowfallCm48h == null ? "Unavailable" : `${i.snowfallCm48h.toFixed(0)} cm` },
    { label: "Temp", value: i.tempMinC == null || i.tempMaxC == null ? "Unavailable" : `${i.tempMinC.toFixed(0)}° to ${i.tempMaxC.toFixed(0)}°` },
    { label: "Wind", value: i.windSpeedKmh == null ? "Unavailable" : `${i.windSpeedKmh.toFixed(0)} km/h` },
  ];
  if (i.snowDepthCm != null && i.snowDepthCm > 0) cells.push({ label: "Depth", value: `${i.snowDepthCm.toFixed(0)} cm` });
  if (i.visibilityLabel) {
    const cell: MetricCell = { label: "Vis", value: i.visibilityLabel };
    if (i.visibilityHours && i.visibilityHours.length > 0) cell.detail = { kind: "visHours", series: i.visibilityHours };
    cells.push(cell);
  }
  if (i.freezingLevelM != null) cells.push({ label: "Freeze", value: `${i.freezingLevelM.toFixed(0)} m` });
  return cells;
}

export function buildMtbMetrics(i: MtbMetricInput): MetricCell[] {
  const cells: MetricCell[] = [];
  if (i.apparentTempC != null) cells.push({ label: "Temp", value: `${i.apparentTempC.toFixed(0)}°C`, sublabel: "feels like" });
  if (i.soilMoistureLabel) cells.push({ label: "Trail", value: i.soilMoistureLabel });
  cells.push({ label: "Wind", value: `${i.windSpeedKmh.toFixed(0)} km/h` });
  if (i.precipMm24h != null) cells.push({ label: "Rain 24h", value: `${i.precipMm24h.toFixed(1)} mm` });
  return cells;
}

export function buildCoastalMetrics(i: CoastalMetricInput): MetricCell[] {
  const cells: MetricCell[] = [];
  if (i.waveHeightM != null) cells.push({ label: "Wave", value: `${i.waveHeightM.toFixed(1)} m` });
  if (i.swellPeriodS != null) cells.push({ label: "Period", value: `${i.swellPeriodS.toFixed(0)} s` });
  if (i.swellDirLabel) cells.push({ label: "Swell", value: i.swellDirLabel });
  if (i.windSpeedKmh != null)
    cells.push({ label: "Wind", value: `${i.windSpeedKmh.toFixed(0)} km/h`, sublabel: i.windLabel ?? undefined });
  if (i.waterTempC != null) cells.push({ label: "Water", value: `${i.waterTempC.toFixed(0)}°C` });
  if (i.tideLabel) cells.push({ label: "Tide", value: i.tideLabel });
  return cells;
}

/** The first readings answer the decision for this sport, rather than every sport sharing a surf strip. */
export function buildSportCoastalMetrics(i: CoastalMetricInput & {
  sport: string;
  windText: string;
  gustText: string;
  secchiDepthM?: number | null;
  currentVelocityMs?: number | null;
  windWaveHeightM?: number | null;
  windWaveDirection?: string | null;
  tideApproximate?: boolean;
}): MetricCell[] {
  const metric = (label: string, value: number | null | undefined, unit: string, decimals = 1, sublabel?: string): MetricCell => ({
    label, value: value == null || !Number.isFinite(value) ? "Unavailable" : `${value.toFixed(decimals)} ${unit}`, ...(sublabel ? { sublabel } : {}),
  });
  const swell = metric("Swell", i.waveHeightM, "m", 1, [i.swellPeriodS == null ? null : `${i.swellPeriodS.toFixed(0)} s`, i.swellDirLabel].filter(Boolean).join(" · "));
  const wind: MetricCell = { label: "Wind", value: i.windText, sublabel: i.windLabel ?? "Shore direction unavailable" };
  const gust: MetricCell = { label: "Gusts", value: i.gustText };
  const water = metric("Water", i.waterTempC, "°C");
  const current = metric("Current", i.currentVelocityMs, "m/s", 2, "Modelled offshore");
  const tide: MetricCell = { label: "Tide", value: i.tideLabel ?? "Unavailable", sublabel: i.tideApproximate ? "Approximate timing" : "Modelled timing" };
  const bumps = metric("Wind waves", i.windWaveHeightM, "m", 1, i.windWaveDirection ?? "Hourly offshore estimate");
  switch (i.sport) {
    case "wind": case "wing-foil": return [wind, gust, swell, water];
    case "downwind-foil": case "sup-downwind": return [bumps, wind, gust, current, water];
    case "diving": return [metric("Water clarity", i.secchiDepthM, "m", 1, "Secchi proxy, not dive visibility"), current, { ...swell, label: "Entry swell" }, water];
    case "swim": case "swimming": return [water, { ...swell, label: "Sea state" }, current, wind];
    case "sup": case "sup-touring": return [wind, gust, { ...swell, label: "Sea state" }, current, water];
    case "foil": return [swell, wind, tide, water];
    default: return [swell, wind, tide, water];
  }
}
